import {HttpBackend, HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {SecureStorageService} from './secure-storage.service';
import {firstValueFrom} from 'rxjs';

interface ClientCredentialsTokenResponse {
  payload?: {
    access_token?: string;
    expires_in?: number;
    token_type?: string;
  };
}

const STORAGE_TOKEN_CLIENT_CREDENTIALS = 'TOKEN_CLIENT_CREDENTIALS';
const STORAGE_EXPIRACION_CLIENT_CREDENTIALS = 'TOKEN_CLIENT_CREDENTIALS_EXPIRACION';
const MARGEN_RENOVACION_MS = 60_000;

@Injectable({providedIn: 'root'})
export class ClientCredentialsTokenService {
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly storage = inject(SecureStorageService);
  private tokenEnMemoria: string | null = null;
  private expiracionEnMemoria = 0;
  private solicitudEnCurso: Promise<string> | null = null;
  private almacenamientoCargado = false;

  async obtenerToken(forzarRenovacion = false): Promise<string> {
    await this.cargarTokenAlmacenado();

    if (!forzarRenovacion && this.tokenEsVigente()) {
      return this.tokenEnMemoria!;
    }

    if (!this.solicitudEnCurso) {
      this.solicitudEnCurso = this.solicitarToken()
        .finally(() => this.solicitudEnCurso = null);
    }

    return this.solicitudEnCurso;
  }

  limpiar(): void {
    this.tokenEnMemoria = null;
    this.expiracionEnMemoria = 0;
    this.storage.removeItem(STORAGE_TOKEN_CLIENT_CREDENTIALS);
    this.storage.removeItem(STORAGE_EXPIRACION_CLIENT_CREDENTIALS);
  }

  private async solicitarToken(): Promise<string> {
    const configuracion = environment.clientCredentials;

    if (!configuracion.client_id || !configuracion.client_secret) {
      throw new Error('Faltan client_id o client_secret en el environment actual');
    }

    const response = await firstValueFrom(this.http.post<ClientCredentialsTokenResponse>(
      environment.server + configuracion.tokenEndpoint.replace(/^\//, ''),
      {
        payload: {
          grant_type: configuracion.grant_type,
          client_id: configuracion.client_id,
          client_secret: configuracion.client_secret
        }
      }
    ));

    const token = response?.payload?.access_token?.trim();
    const expiresIn = Number(response?.payload?.expires_in);

    if (!token || !Number.isFinite(expiresIn) || expiresIn <= 0) {
      throw new Error('El servicio client_credentials no devolvió un token válido');
    }

    this.tokenEnMemoria = token;
    this.expiracionEnMemoria = Date.now() + expiresIn * 1000;

    await Promise.all([
      this.storage.setItem(STORAGE_TOKEN_CLIENT_CREDENTIALS, token),
      this.storage.setItem(
        STORAGE_EXPIRACION_CLIENT_CREDENTIALS,
        this.expiracionEnMemoria.toString()
      )
    ]);

    return token;
  }

  private async cargarTokenAlmacenado(): Promise<void> {
    if (this.almacenamientoCargado) return;

    this.almacenamientoCargado = true;
    const [token, expiracion] = await Promise.all([
      this.storage.getItem(STORAGE_TOKEN_CLIENT_CREDENTIALS),
      this.storage.getItem(STORAGE_EXPIRACION_CLIENT_CREDENTIALS)
    ]);

    this.tokenEnMemoria = token;
    this.expiracionEnMemoria = Number(expiracion) || 0;

    if (!this.tokenEsVigente()) {
      this.limpiar();
    }
  }

  private tokenEsVigente(): boolean {
    return Boolean(this.tokenEnMemoria) &&
      Date.now() < this.expiracionEnMemoria - MARGEN_RENOVACION_MS;
  }
}
